import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from './message.service';
import { SynchronizationService } from './synchronization.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { DefinitionOfDone } from '../models/definitionOfDone';
import { TaskDefinitionOfDone } from '../models/taskDefinitionOfDone';
import { Issue } from '../models/issue';
import { IssuecardComponent } from '../components/issuecard/issuecard.component';

/**
 * this class represents access to all rest requests concerning Definition of Done data of the PyWall
 */
@Injectable()
export class DefinitionOfDoneService {

  private projectBaseURL: string;

  public newDoDPopUp: Subject<Issue> = new Subject;
  public removeDoDPopUp: Subject<Issue> = new Subject;
  newDoDPopUpObserver = this.newDoDPopUp.asObservable();
  removeDoDPopUpObserver = this.removeDoDPopUp.asObservable();

  emitNewDoDPopup(issue: Issue) {
    this.newDoDPopUp.next(issue);
  }

  emitNewDoDPopupFromIssuecard(issueCard: IssuecardComponent, oldIssue: Issue) {
    if (issueCard.issue.status.name === 'Done' && oldIssue.status.name !== 'Done') {
      console.log('ready to set up');
      this.newDoDPopUp.next(issueCard.issue);
    }
  }

  emitRemoveDoDPopUp(issue: Issue) {
    this.removeDoDPopUp.next(issue);
  }

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private configService: ConfigLoaderService,
    private sync: SynchronizationService,
  ) {
    this.projectBaseURL = `${this.configService.getSettings().pyWallServer.url}projects`;
  }

  /**
   * Get all Definition Of Done for a project
   * @param projectId
   * @param {boolean} when set, deleted "Definition of Done" are requested too
   * @returns
   */
  getDefinitionOfDone(projectId: number, deleted = true): Observable<DefinitionOfDone[]> {
    this.messageService.sendDebug(`DefinitionOfDoneService getDefinitionOfDone called on project ${projectId}`);
    let params = new HttpParams();
    params = params.append('includeDeleted', String(deleted));
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/definitionOfDone/definitionOfDones/`;
    return this.http.get<DefinitionOfDone[]>(targetURL, { params: params }).pipe(
      map(res => this.mapJSONToDefinitionOfDone(res)));
  }

  addDefinitionOfDone(projectId: number, definitionOfDone: DefinitionOfDone): Observable<DefinitionOfDone> {
    this.messageService.sendDebug(`addDefinitionOfDone called on project ${projectId}`);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/definitionOfDone/definitionOfDones/`;
    return this.http.post<DefinitionOfDone>(targetURL, definitionOfDone).pipe(
      map(val => new DefinitionOfDone(val['id'], val['projectId'], val['name'], val['active'], val['deleted'])));
  }

  updateDefinitionOfDone(projectId: number, definitionOfDone: DefinitionOfDone): void {
    this.messageService.sendDebug(`updateDefinitionOfDone called on project ${projectId} and definitionOfDoneID: ${definitionOfDone.id}`);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/definitionOfDone/definitionOfDones/${definitionOfDone.id}/`;
    this.http.put(targetURL, definitionOfDone).subscribe({
      next: () => {
        this.sync.sendEvent('updateDefinitionOfDone', definitionOfDone);
      },
      error: (error) => {
        this.messageService.sendWarn(`Couldn't update definitionOfDone ${definitionOfDone.id} on project ${projectId}, ${error}`);
      }
    });
  }

  deleteDefinitionOfDone(projectId: number, definitionOfDone: DefinitionOfDone): Observable<DefinitionOfDone> {
    this.messageService.sendDebug(`deleteDefinitonOfDone called on project ${projectId} and definitionOfDoneID: ${definitionOfDone.id}`);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/definitionOfDone/definitionOfDones/${definitionOfDone.id}/`;
    return this.http.delete<DefinitionOfDone>(targetURL).pipe(
      map(val => new DefinitionOfDone(val['id'], val['projectId'], val['name'], val['active'], val['deleted'])));
  }

  getDefinitionOfDoneForTask(projectId: number, taskId: number): Observable<TaskDefinitionOfDone> {
    this.messageService.sendDebug(`DefinitionOfDoneService getDefinitionOfDone called on project ${projectId}`);
    const targetURL = this.projectBaseURL + `/${projectId}/tasks/${taskId}/definitionOfDone/`;
    return this.http.get<TaskDefinitionOfDone>(targetURL).pipe(
      map(res => this.mapJSONToDefinitionOfDoneForTasks(res)));
  }

  addDefinitionOfDoneForTask(projectId: number, taskId: number,
    taskDefinitionOfDone: TaskDefinitionOfDone): Observable<TaskDefinitionOfDone> {
    this.messageService.sendDebug(`addTaskDefinitionOfDone called on project ${projectId}`);
    const targetURL = this.projectBaseURL + `/${projectId}/tasks/${taskId}/definitionOfDone/`;
    return this.http.post<TaskDefinitionOfDone>(targetURL, taskDefinitionOfDone);
  }

  updateDefinitionOfDoneForTask(projectId: number, taskId: number, taskDefinitionOfDone: TaskDefinitionOfDone): void {
    const debugMsg = `updateDefinitionOfDoneForTask called on project ${projectId} and definitionOfDoneID: ${taskDefinitionOfDone.id}`;
    this.messageService.sendDebug(debugMsg);
    const targetURL = this.projectBaseURL + `/${projectId}/tasks/${taskId}/definitionOfDone/${taskDefinitionOfDone.id}/`;
    this.http.put(targetURL, taskDefinitionOfDone).subscribe({
      next: () => {
        this.sync.sendEvent('updateDefinitionOfDoneForTask', taskDefinitionOfDone);
      },
      error: (error) => {
        this.messageService.sendWarn(`Couldn't update definitionOfDone ${taskDefinitionOfDone.id} on project ${projectId}, ${error}`);
      }
    });
  }

  mapJSONToDefinitionOfDone(data: any): DefinitionOfDone[] {
    const definitionOfDone = [];
    for (const val of data) {
      const definitionOfDoneData = new DefinitionOfDone(val['id'], val['projectId'], val['name'], val['active'], val['deleted']);
      definitionOfDone.push(definitionOfDoneData);
    }
    return definitionOfDone;
  }

  mapJSONToDefinitionOfDoneForTasks(data: any): TaskDefinitionOfDone {
    return new TaskDefinitionOfDone(data['id'], data['task_id'], data['definition_of_dones_id']);
  }
}
